import {emailLinkRefusalOf, type EmailLinkRefusal} from "api/apiErrorIds";
import {serverMessage} from "utils/ErrorUtils";
import type {StopRemindersOutcome} from "./stopReminders";

/**
 * The unfinished-signup reminder's landing page (ui#1688): what `/login` shows when its URL fragment
 * carried one of the reminder's two links, and what each press does. Deliberately independent of
 * React — the rules are here, and the card only draws the state it is handed.
 *
 * **NOTHING HAPPENS ON A PAGE LOAD.** Mail scanners follow every URL in a message, so arriving at the
 * page must cost the reader nothing: a landing only ever produces a card with a button on it, and the
 * `finishing` / `stopping` states are reachable ONLY through {@link pressedConfirm} / {@link pressedStop}.
 * That is the whole reason this replaced the effect that redeemed the link the moment the page opened.
 *
 * The two tokens are NOT interchangeable, and the fine print's stop control depends on that: the api
 * signs each under its own purpose prefix ("signup-finish:" / "signup-stop:") precisely "so one can
 * never verify as the other under the shared secret" (transferify-api-service
 * `SignupFinishLinkTokens`). A finish token posted to `/auth/email-code/stop` is therefore refused as
 * an invalid link, so {@link pressedStop} can only act on a stop token the fragment actually carried.
 */
export type SignupLinkState =
	/** No reminder token in the fragment: the ordinary login page, unchanged. */
	| {kind: "none"}
	/** A finish link landed. `stopToken` is the fine print's, present only when the fragment carried one. */
	| {kind: "confirm"; token: string; stopToken: string | null}
	/** The reader pressed confirm; the finish POST is in flight. */
	| {kind: "finishing"; token: string}
	/** The api verified the link; the Firebase sign-in and the session are settling. */
	| {kind: "signingIn"; email: string | null}
	/**
	 * The app session is authenticated: either the redemption completed, or the browser was already
	 * signed in when the link landed and the link had nothing to add. The page continues to the
	 * post-login destination from here; `email` is null when this page never learned one.
	 */
	| {kind: "signedIn"; email: string | null}
	/** A stop link landed, waiting for its own explicit press. */
	| {kind: "stopPrompt"; token: string}
	/** The reader pressed stop; the stop POST is in flight. */
	| {kind: "stopping"; token: string}
	/**
	 * The unfinished signup gets no further reminder: it was deleted, or kept because a sender saved its address as a
	 * contact, with its reminders stopped.
	 */
	| {kind: "stopped"}
	/** The link verified but there was nothing left to stop. A success for the reader, not an error. */
	| {kind: "nothingToStop"}
	/**
	 * The api refused the link.
	 *
	 * `from` is which link was refused, and it decides where the reader goes: a refused FINISH sends
	 * them to the ordinary code form, because the mail they are holding cannot help them any more and
	 * the form can; a refused STOP stays on the stop card, because that reader asked to be left alone
	 * and answering them with a sign-in prompt is the one thing this path must not do.
	 *
	 * `message` is the api's OWN already-localized sentence, null only when the server never answered.
	 * `refusal` names which refusal it was when the api said so, so the routing decision is made on the
	 * stable id rather than on prose.
	 */
	| {
		kind: "refused";
		from: "finish" | "stop";
		message: string | null;
		refusal: EmailLinkRefusal | null;
	};

/**
 * The state a page load produces from the fragment's tokens. A finish link wins over a stop token that
 * arrived with it: the stop token is then the fine print's, not a competing card.
 */
export function linkLanding(finishToken: string | null, stopToken: string | null): SignupLinkState {
	if (finishToken !== null) {
		return {kind: "confirm", token: finishToken, stopToken};
	}
	return stopToken !== null ? {kind: "stopPrompt", token: stopToken} : {kind: "none"};
}

/** Whether the confirm press is on offer, i.e. exactly on an un-pressed confirm card. */
export function confirmAvailable(state: SignupLinkState): boolean {
	return state.kind === "confirm";
}

/**
 * Whether the finish card is showing its Confirm press, pressed or not: the states in which the controls
 * standing above it are on the card, the marketing objection box among them (kitecorp/transferify-ui#3503).
 * `signingIn` is not one of them: a sentence has replaced the press by then.
 */
export function confirmOnScreen(state: SignupLinkState): boolean {
	return state.kind === "confirm" || state.kind === "finishing";
}

/**
 * Which card owns this state: the stop card, or the finish card.
 *
 * A rule rather than a render-time condition, because it decides where a REFUSAL is shown, and getting
 * that wrong answers somebody who asked to be left alone with a sign-in prompt.
 */
export function stopSurface(state: SignupLinkState): boolean {
	return state.kind === "stopPrompt" || state.kind === "stopping" || state.kind === "stopped"
		|| state.kind === "nothingToStop" || (state.kind === "refused" && state.from === "stop");
}

/**
 * Whether the press has produced its OUTCOME — the states in which the control the reader pressed is
 * gone from the card and a sentence stands in its place.
 *
 * A rule rather than a render-time condition because it drives two things the card owes an assistive
 * reader: what gets announced, and where keyboard focus goes when the button it was on is unmounted.
 * `finishing` and `stopping` are deliberately NOT here — the button is still on screen, merely busy.
 */
export function outcomeReached(state: SignupLinkState): boolean {
	return state.kind === "signingIn" || state.kind === "stopped"
		|| state.kind === "nothingToStop" || state.kind === "refused";
}

/** Whether a stop press is on offer: the stop card, or a confirm card that carried a stop token. */
export function stopAvailable(state: SignupLinkState): boolean {
	return state.kind === "stopPrompt" || (state.kind === "confirm" && state.stopToken !== null);
}

/**
 * Starts the finish. Only from the confirm card, so a second press cannot spend the link twice — the
 * guard is in the transition rather than in the view, where two clicks in one frame can both pass.
 */
export function pressedConfirm(state: SignupLinkState): SignupLinkState {
	return state.kind === "confirm" ? {kind: "finishing", token: state.token} : state;
}

/** Starts the stop, from whichever card offers it. Any other state stays put, for the same reason. */
export function pressedStop(state: SignupLinkState): SignupLinkState {
	if (state.kind === "stopPrompt") {
		return {kind: "stopping", token: state.token};
	}
	if (state.kind === "confirm" && state.stopToken !== null) {
		return {kind: "stopping", token: state.stopToken};
	}
	return state;
}

/**
 * The api verified the link. The address is the one it echoed back rather than anything this page
 * knew: the token is opaque, so this is the first and only moment the reader's address exists here.
 */
export function finishAccepted(state: SignupLinkState, email: string | null): SignupLinkState {
	return state.kind === "finishing" ? {kind: "signingIn", email} : state;
}

/**
 * The api refused the link, or never answered.
 *
 * Reachable from `signingIn` as well as `finishing`: a link can verify and THEN fail, when the api
 * mints a custom token Firebase will not take. `serverMessage` answers null for that one, which is
 * how the card knows to fall back to its own copy instead of showing an empty sentence.
 */
export function finishRefused(state: SignupLinkState, error: unknown): SignupLinkState {
	if (state.kind !== "finishing" && state.kind !== "signingIn") {
		return state;
	}
	return {kind: "refused", from: "finish", message: serverMessage(error), refusal: emailLinkRefusalOf(error)};
}

/**
 * The stop call settled. A refusal has no identity attached: `/auth/email-code/stop` answers a single
 * 400 for every unverifiable link, so there is nothing to tell apart.
 */
export function stopSettled(state: SignupLinkState, outcome: StopRemindersOutcome): SignupLinkState {
	if (state.kind !== "stopping") {
		return state;
	}
	if (outcome.kind === "refused") {
		return {kind: "refused", from: "stop", message: outcome.message, refusal: null};
	}
	return outcome.kind === "stopped" ? {kind: "stopped"} : {kind: "nothingToStop"};
}

/**
 * Folds in the settled auth session, which ends the finish path in BOTH the ways it can end well: the
 * redemption's own sign-in landing, and a browser that was already signed in when the link arrived
 * (which the auto-redeeming effect handled before this page existed).
 *
 * Keyed on the app session rather than on the redemption promise because the session is the thing the
 * page is waiting for, and it is the same answer either way.
 *
 * The stop card is deliberately untouched by it: that reader asked to be left alone, and being signed
 * in elsewhere is not an answer to that. A POST still in flight is untouched too, so a sign-in that is
 * merely under way cannot be mistaken for one that landed.
 */
export function sessionSettled(state: SignupLinkState, authenticated: boolean): SignupLinkState {
	if (!authenticated) {
		return state;
	}
	if (state.kind === "confirm") {
		return {kind: "signedIn", email: null};
	}
	return state.kind === "signingIn" ? {kind: "signedIn", email: state.email} : state;
}
